import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { API, formatCurrency } from '../config';
import { exportToPDF, exportToExcel } from '../utils/exportUtils';
import { MonthlyBarChart } from './MonthlyBarChart';

const MetricDrillDownModal = ({ isOpen, onClose, plantId, plantName, year, metricKey, metricLabel, token }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // For the 'power' metric we surface an additional inverse-efficiency view:
  // kgs / Unit = (production_kgs) / (power_units). Helpful for operators who
  // think about throughput-per-energy rather than energy-per-throughput.
  const isPower = metricKey === 'power';

  const powerInverse = useMemo(() => {
    if (!data || !isPower) return null;
    const monthly = (data.monthly_data || []).map((m) => ({
      month: m.month,
      month_name: m.month_name,
      // kgs / Unit (only when power was consumed); guard against div-by-zero
      value: m.consumption && m.consumption > 0
        ? +((m.production_mt * 1000) / m.consumption).toFixed(2)
        : 0,
    }));
    const totalConsumption = data.summary.total_consumption || 0;
    const totalProductionKgs = (data.summary.total_production_mt || 0) * 1000;
    const avg = totalConsumption > 0
      ? +(totalProductionKgs / totalConsumption).toFixed(2)
      : 0;
    return { monthly, avg };
  }, [data, isPower]);

  const loadMetricData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/metrics/${plantId}/${year}/${metricKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load metric data');
    } finally {
      setLoading(false);
    }
  }, [plantId, year, metricKey, token]);

  useEffect(() => {
    if (isOpen && plantId && metricKey) {
      loadMetricData();
    }
  }, [isOpen, plantId, year, metricKey, loadMetricData]);

  const handleExportPDF = () => {
    if (!data) return;
    const columns = ['Month', 'Production (MT)', 'Consumption', `${data.unit}`, 'Cost (Rs)', 'Rs/MT'];
    const rows = data.monthly_data.map(m => [
      m.month_name, m.production_mt || '-', m.consumption || '-', m.per_mt || '-',
      m.cost || '-', m.cost_per_mt || '-'
    ]);
    rows.push(['TOTAL', data.summary.total_production_mt, data.summary.total_consumption,
      data.summary.avg_per_mt, data.summary.total_cost, data.summary.avg_cost_per_mt]);
    exportToPDF(
      `${metricLabel} - Monthly Breakdown`,
      `${plantName} | Year: ${year}`,
      columns, rows,
      `${metricKey}_breakdown_${plantName}_${year}`
    );
  };

  const handleExportExcel = () => {
    if (!data) return;
    const columns = ['Month', 'Production (MT)', 'Consumption', `${data.unit}`, 'Cost (Rs)', 'Rs/MT'];
    const rows = data.monthly_data.map(m => [
      m.month_name, m.production_mt, m.consumption, m.per_mt, m.cost, m.cost_per_mt
    ]);
    rows.push(['TOTAL', data.summary.total_production_mt, data.summary.total_consumption,
      data.summary.avg_per_mt, data.summary.total_cost, data.summary.avg_cost_per_mt]);
    exportToExcel(
      `${metricLabel} - ${plantName} - ${year}`,
      columns, rows,
      `${metricKey}_breakdown_${plantName}_${year}`
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#156ACA] to-[#0F5299] text-white p-4 sm:p-6 sm:rounded-t-xl z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold truncate">{metricLabel}</h2>
              <p className="text-xs sm:text-base text-blue-200 mt-0.5 truncate">{plantName} - {year}</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {data && (
                <>
                  <button onClick={handleExportPDF}
                    className="bg-white/20 hover:bg-white/30 text-white px-2 sm:px-3 py-2 rounded-lg transition-colors flex items-center text-xs sm:text-sm"
                    data-testid="export-pdf-btn">
                    <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">PDF</span>
                  </button>
                  <button onClick={handleExportExcel}
                    className="bg-[#56BB31]/80 hover:bg-[#56BB31] text-white px-2 sm:px-3 py-2 rounded-lg transition-colors flex items-center text-xs sm:text-sm"
                    data-testid="export-excel-btn">
                    <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excel</span>
                  </button>
                </>
              )}
              <button onClick={onClose}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#156ACA]"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
          )}

          {!loading && !error && data && (
            <>
              {/* Summary Cards */}
              <div className={isPower ? 'grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4' : 'grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4'}>
                <div className="bg-[#E8F2FC] p-3 sm:p-4 rounded-lg border border-[#156ACA]/20">
                  <p className="text-xs sm:text-sm text-[#156ACA]">Total Consumption</p>
                  <p className="text-lg sm:text-2xl font-bold text-[#0F5299] break-words">{data.summary.total_consumption.toLocaleString()}</p>
                </div>
                <div className="bg-[#EEF9E8] p-3 sm:p-4 rounded-lg border border-[#56BB31]/20">
                  <p className="text-xs sm:text-sm text-[#56BB31]">Avg {data.unit}</p>
                  <p className="text-lg sm:text-2xl font-bold text-[#429424] break-words">{data.summary.avg_per_mt}</p>
                </div>
                {isPower && powerInverse ? (
                  <div className="bg-teal-50 p-3 sm:p-4 rounded-lg border border-teal-200" data-testid="drilldown-avg-kgs-per-unit">
                    <p className="text-xs sm:text-sm text-teal-600">Avg kgs / Unit</p>
                    <p className="text-lg sm:text-2xl font-bold text-teal-800 break-words">{powerInverse.avg}</p>
                  </div>
                ) : null}
                <div className="bg-orange-50 p-3 sm:p-4 rounded-lg border border-orange-200">
                  <p className="text-xs sm:text-sm text-orange-600">Total Cost</p>
                  <p className="text-lg sm:text-2xl font-bold text-orange-800 break-words">{formatCurrency(data.summary.total_cost)}</p>
                </div>
                <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-200">
                  <p className="text-xs sm:text-sm text-purple-600">Avg Rs/MT</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-800 break-words">{formatCurrency(data.summary.avg_cost_per_mt)}</p>
                </div>
              </div>

              {/* Chart - Consumption per MT */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly {data.unit} Trend</h3>
                <MonthlyBarChart
                  data={data.monthly_data.map((m) => ({ month: m.month, month_name: m.month_name, value: m.per_mt }))}
                  color="#156ACA"
                  valueLabel={data.unit}
                  formatValue={(v) => `${v} ${data.unit}`}
                />
              </div>

              {/* Chart - kgs/Unit inverse (power only) */}
              {isPower && powerInverse ? (
                <div className="bg-white rounded-lg border p-6" data-testid="drilldown-kgs-per-unit-chart">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly kgs / Unit Trend</h3>
                  <p className="text-xs text-gray-500 mb-3">Production per unit of power consumed — higher is better (inverse of Units/MT).</p>
                  <MonthlyBarChart
                    data={powerInverse.monthly}
                    color="#0D9488"
                    valueLabel="kgs/Unit"
                    formatValue={(v) => `${v} kgs/Unit`}
                  />
                </div>
              ) : null}

              {/* Chart - Cost per MT */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Rs/MT Trend</h3>
                <MonthlyBarChart
                  data={data.monthly_data.map((m) => ({ month: m.month, month_name: m.month_name, value: m.cost_per_mt }))}
                  color="#56BB31"
                  valueLabel="Rs/MT"
                  formatValue={(v) => `Rs ${v.toLocaleString('en-IN')}`}
                />
              </div>

              {/* Monthly Table */}
              <div className="bg-white rounded-lg border overflow-hidden">
                <h3 className="text-lg font-semibold text-gray-900 p-4 border-b bg-gray-50">Monthly Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Production (MT)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Consumption</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{data.unit}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost (Rs)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rs/MT</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.monthly_data.map((month) => (
                        <tr key={month.month} className={month.production_mt > 0 ? '' : 'bg-gray-50 text-gray-400'}>
                          <td className="px-4 py-3 font-medium">{month.month_name}</td>
                          <td className="px-4 py-3 text-right">{month.production_mt || '-'}</td>
                          <td className="px-4 py-3 text-right">{month.consumption || '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold text-[#156ACA]">{month.per_mt || '-'}</td>
                          <td className="px-4 py-3 text-right">{month.cost ? formatCurrency(month.cost) : '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold text-[#56BB31]">{month.cost_per_mt ? formatCurrency(month.cost_per_mt) : '-'}</td>
                        </tr>
                      ))}
                      <tr className="bg-[#E8F2FC] font-bold">
                        <td className="px-4 py-3">TOTAL</td>
                        <td className="px-4 py-3 text-right">{data.summary.total_production_mt}</td>
                        <td className="px-4 py-3 text-right">{data.summary.total_consumption}</td>
                        <td className="px-4 py-3 text-right text-[#156ACA]">{data.summary.avg_per_mt}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(data.summary.total_cost)}</td>
                        <td className="px-4 py-3 text-right text-[#56BB31]">{formatCurrency(data.summary.avg_cost_per_mt)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetricDrillDownModal;
